import { useState, useMemo } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { format } from 'date-fns';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useFieldArray } from 'react-hook-form';
import { z } from 'zod';
import { ArrowLeft, Plus, Trash2, Loader2, Check, ChevronsUpDown, Search, Users, Banknote, CreditCard, Wallet, CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ProtectedRoute } from '@/components/protected-route';
import { MainLayout } from '@/components/main-layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { PageHeader } from '@/components/page-header';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from '@/components/ui/table';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useCustomers } from '@/hooks/use-customers';
import { useServices } from '@/hooks/use-services';
import { useCreateOrder } from '@/hooks/use-orders';
import { useToast } from '@/hooks/use-toast';
import type { ApiCustomer, ApiService } from '@/types';

// ─── Validation Schema ───────────────────────────────────
const orderItemSchema = z.object({
  serviceId: z.string().uuid('Service is required'),
  quantity: z.number().positive('Quantity must be greater than 0'),
  unitPrice: z.number().positive('Unit price must be greater than 0'),
});

const newOrderSchema = z
  .object({
    customerId: z.string().uuid('Customer is required'),
    orderDate: z.date(),
    items: z.array(orderItemSchema).min(1, 'At least one item is required'),
    paymentStatus: z.enum(['PAID', 'UNPAID']),
    paymentMethod: z.string().optional(),
    notes: z.string().max(500).optional(),
  })
  .refine(
    (data) => !(data.paymentStatus === 'PAID' && !data.paymentMethod),
    { message: 'Payment method is required when paid', path: ['paymentMethod'] },
  );

type NewOrderFormData = z.infer<typeof newOrderSchema>;

export const Route = createFileRoute('/orders/new')({
  component: () => (
    <ProtectedRoute>
      <MainLayout>
        <NewOrderPage />
      </MainLayout>
    </ProtectedRoute>
  ),
});

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  }).format(amount);
}

function NewOrderPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [openCustomerCombobox, setOpenCustomerCombobox] = useState(false);
  const [openServiceCombobox, setOpenServiceCombobox] = useState(false);

  // Fetch data
  const { data: customersResponse, isLoading: isLoadingCustomers } = useCustomers({ page: 1, limit: 100 } as any);
  const customers = useMemo(() => customersResponse?.data || [], [customersResponse?.data]);
  const { data: services, isLoading: isLoadingServices } = useServices();
  const createOrder = useCreateOrder();

  const form = useForm<NewOrderFormData>({
    resolver: zodResolver(newOrderSchema),
    defaultValues: {
      customerId: '',
      orderDate: new Date(),
      items: [],
      paymentStatus: 'UNPAID',
      paymentMethod: undefined,
      notes: '',
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'items',
  });

  // eslint-disable-next-line react-hooks/incompatible-library
  const watchedItems = form.watch('items');
  const paymentStatus = form.watch('paymentStatus');
  const customerId = form.watch('customerId');

  // Memoize selected customer
  const selectedCustomer = useMemo(() => {
    if (!customerId) return null;
    return customers.find((c) => String(c.id) === customerId);
  }, [customers, customerId]);

  // Calculate totals
  const totalAmount = useMemo(() => {
    return (watchedItems || []).reduce(
      (sum, item) => sum + (item.quantity || 0) * (item.unitPrice || 0),
      0,
    );
  }, [watchedItems]);

  const handleAddService = (service: ApiService) => {
    append({
      serviceId: String(service.id),
      quantity: 1,
      unitPrice: service.unitPrice,
    });
    setOpenServiceCombobox(false);
  };

  const handleQuantityChange = (index: number, value: string) => {
    const qty = parseFloat(value) || 0;
    form.setValue(`items.${index}.quantity`, qty, { shouldValidate: true });
  };

  const handleUnitPriceChange = (index: number, value: string) => {
    const price = parseFloat(value) || 0;
    form.setValue(`items.${index}.unitPrice`, price, { shouldValidate: true });
  };

  const onSubmit = async (data: NewOrderFormData) => {
    try {
      const result = await createOrder.mutateAsync({
        customerId: data.customerId,
        orderDate: format(data.orderDate, 'yyyy-MM-dd'),
        items: data.items.map((i) => ({
          serviceId: i.serviceId,
          quantity: i.quantity,
          unitPrice: i.unitPrice,
        })),
        paymentStatus: data.paymentStatus,
        paymentMethod: data.paymentMethod,
        notes: data.notes || undefined,
      });

      toast({
        title: 'Order Created',
        description: `Order ${result.data.order.orderNumber} created successfully!`,
      });

      navigate({ 
        to: '/orders', 
        search: (prev: any) => ({ ...prev, orderId: result.data.order.id }),
        replace: true 
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create order',
        variant: 'destructive',
      });
    }
  };

  if (isLoadingCustomers || isLoadingServices) {
    return (
      <div className="flex justify-center items-center py-24">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="flex max-w-4xl min-w-0 flex-col gap-6">
      {/* Header */}
      <PageHeader
        title="Create New Order"
        description="Create a new laundry order for a customer"
        actions={(
          <Link to="/orders" search={{}}>
            <Button variant="outline" className="w-full sm:w-auto">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Orders
            </Button>
          </Link>
        )}
      />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Customer Selection */}
          <Card className="border-none shadow-sm bg-muted/40">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-bold">Customer Information</CardTitle>
                  <CardDescription>Assign a customer to this transaction</CardDescription>
                </div>
                {customerId && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs h-8 text-muted-foreground hover:text-primary"
                    onClick={() => form.setValue('customerId', '')}
                    type="button"
                  >
                    Change Customer
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <FormField
                control={form.control}
                name="customerId"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      {!customerId ? (
                        <Popover open={openCustomerCombobox} onOpenChange={setOpenCustomerCombobox}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              className="w-full justify-between h-14 px-4 border-dashed border-2 hover:border-primary hover:bg-primary/5 transition-all group"
                              type="button"
                            >
                              <div className="flex items-center gap-3">
                                <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center group-hover:bg-primary/10 group-hover:text-primary transition-colors">
                                  <Users className="h-4 w-4" />
                                </div>
                                <span className="text-muted-foreground font-medium italic">
                                  Click to select or search customer...
                                </span>
                              </div>
                              <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[calc(100vw-2rem)] max-w-[420px] p-0 shadow-2xl border-none" align="start">
                            <Command className="rounded-xl border">
                              <div className="flex items-center border-b px-3">
                                <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                                <CommandInput
                                  placeholder="Search name, phone, or ID..."
                                  className="h-12 border-none focus:ring-0"
                                />
                              </div>
                              <CommandList className="max-h-[300px]">
                                <CommandEmpty className="py-6 text-center text-sm">
                                  <div className="flex flex-col items-center gap-2">
                                    <Users className="h-8 w-8 text-muted-foreground/30" />
                                    <span>No customer found.</span>
                                  </div>
                                </CommandEmpty>
                                <CommandGroup heading="Customers" className="px-2">
                                  {customers.map((c: ApiCustomer) => (
                                    <CommandItem
                                      key={c.id}
                                      value={`${c.name} ${c.phone || ''} ${c.customerNumber || ''}`}
                                      onSelect={() => {
                                        field.onChange(String(c.id));
                                        setOpenCustomerCombobox(false);
                                      }}
                                      className="rounded-lg mb-1 py-3 cursor-pointer"
                                    >
                                      <div className="flex items-center gap-3 w-full">
                                        <Avatar className="h-10 w-10 border-2 border-background shadow-sm">
                                          <AvatarFallback className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white font-bold">
                                            {c.name.charAt(0)}
                                          </AvatarFallback>
                                        </Avatar>
                                        <div className="flex flex-col flex-1">
                                          <span className="font-bold text-sm">{c.name}</span>
                                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                            <span>{c.phone}</span>
                                            <span className="h-1 w-1 rounded-full bg-muted-foreground/30" />
                                            <span className="font-mono">{c.customerNumber}</span>
                                          </div>
                                        </div>
                                        {field.value === String(c.id) && (
                                          <Check className="h-4 w-4 text-primary" />
                                        )}
                                      </div>
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      ) : (
                        <div className="flex items-center justify-between p-4 bg-background rounded-xl border-2 border-primary/20 shadow-sm animate-in fade-in zoom-in duration-200">
                          <div className="flex items-center gap-4">
                            <Avatar className="h-14 w-14 border-2 border-primary/10">
                              <AvatarFallback className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-xl font-bold">
                                {selectedCustomer?.name.charAt(0)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <h3 className="text-lg font-bold leading-none">
                                  {selectedCustomer?.name}
                                </h3>
                                <Badge
                                  variant="outline"
                                  className="h-5 px-1.5 font-mono text-[10px] tracking-wider uppercase bg-primary/5"
                                >
                                  {selectedCustomer?.customerNumber}
                                </Badge>
                              </div>
                              <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                                <Users className="h-3 w-3" />
                                {selectedCustomer?.phone}
                                {selectedCustomer?.email && (
                                  <>
                                    <span className="h-1 w-1 rounded-full bg-muted-foreground/30" />
                                    {selectedCustomer?.email}
                                  </>
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Order Date */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Order Details</CardTitle>
              <CardDescription>Set the order date</CardDescription>
            </CardHeader>
            <CardContent>
              <FormField
                control={form.control}
                name="orderDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Order Date</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              'w-full pl-3 text-left font-normal sm:w-[240px]',
                              !field.value && 'text-muted-foreground'
                            )}
                            type="button"
                          >
                            {field.value ? (
                              format(field.value, 'PPP')
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Service Items */}
          <Card>
            <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle className="text-lg">Service Items</CardTitle>
                <CardDescription>Add laundry services to the order</CardDescription>
              </div>

              <Popover open={openServiceCombobox} onOpenChange={setOpenServiceCombobox}>
                <PopoverTrigger asChild>
                  <Button
                    variant="default"
                    role="combobox"
                    className="w-full bg-blue-600 hover:bg-blue-700 sm:w-auto"
                    type="button"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Service
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[calc(100vw-2rem)] max-w-[320px] p-0" align="end">
                  <Command>
                    <CommandInput placeholder="Search service..." />
                    <CommandList>
                      <CommandEmpty>No service found.</CommandEmpty>
                      <CommandGroup>
                        {(services || []).map((service: ApiService) => (
                          <CommandItem
                            key={service.id}
                            value={service.name}
                            onSelect={() => handleAddService(service)}
                            className="py-3 px-4 cursor-pointer"
                          >
                            <div className="flex flex-col w-full gap-1">
                              <div className="flex justify-between items-center">
                                <span className="font-semibold text-gray-900">{service.name}</span>
                                <span className="font-bold text-blue-700">
                                  {formatCurrency(service.unitPrice)}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant="secondary" className="text-[10px] h-4 px-1 leading-none uppercase">
                                  {service.unit}
                                </Badge>
                                <span className="text-xs text-muted-foreground">{service.code}</span>
                              </div>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </CardHeader>
            <CardContent>
              {fields.length > 0 ? (
                <div className="overflow-hidden rounded-xl border-2 border-muted shadow-sm bg-background">
                  <Table>
                    <TableHeader className="bg-muted/50">
                      <TableRow className="hover:bg-transparent border-b-2">
                        <TableHead className="w-[30%] font-bold text-xs uppercase tracking-wider text-muted-foreground py-4">
                          Service
                        </TableHead>
                        <TableHead className="text-center font-bold text-xs uppercase tracking-wider text-muted-foreground py-4">
                          Unit
                        </TableHead>
                        <TableHead className="text-center font-bold text-xs uppercase tracking-wider text-muted-foreground py-4">
                          Qty
                        </TableHead>
                        <TableHead className="text-right font-bold text-xs uppercase tracking-wider text-muted-foreground py-4">
                          Unit Price
                        </TableHead>
                        <TableHead className="text-right font-bold text-xs uppercase tracking-wider text-muted-foreground py-4">
                          Subtotal
                        </TableHead>
                        <TableHead className="w-[50px] py-4"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {fields.map((field, index) => {
                        const item = watchedItems?.[index];
                        const service = (services || []).find((s) => String(s.id) === item.serviceId);
                        const subtotal = (item.quantity || 0) * (item.unitPrice || 0);

                        return (
                          <TableRow key={field.id} className="group transition-colors hover:bg-muted/30">
                            <TableCell className="py-4">
                              <div className="font-bold text-sm text-gray-900">
                                {service?.name || 'Unknown Service'}
                              </div>
                              <div className="text-xs text-muted-foreground">{service?.code}</div>
                            </TableCell>
                            <TableCell className="text-center py-4">
                              <Badge variant="outline" className="text-[10px] uppercase">
                                {service?.unit}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center py-4">
                              <Input
                                type="number"
                                min="0.01"
                                step="0.01"
                                value={item.quantity}
                                onChange={(e) => handleQuantityChange(index, e.target.value)}
                                className="w-20 text-center h-8"
                              />
                            </TableCell>
                            <TableCell className="text-right py-4">
                              <Input
                                type="number"
                                min="0"
                                step="100"
                                value={item.unitPrice}
                                onChange={(e) => handleUnitPriceChange(index, e.target.value)}
                                className="w-28 text-right h-8 ml-auto"
                              />
                            </TableCell>
                            <TableCell className="text-right py-4">
                              <span className="font-bold text-blue-700 text-base">
                                {formatCurrency(subtotal)}
                              </span>
                            </TableCell>
                            <TableCell className="py-4">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => remove(index)}
                                className="h-9 w-9 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-full transition-all opacity-0 group-hover:opacity-100"
                                type="button"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                    <TableFooter className="bg-transparent border-t-2 border-muted">
                      <TableRow className="hover:bg-transparent border-none">
                        <TableCell colSpan={4} className="py-6 text-right">
                          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground pt-2">
                            Grand Total
                          </p>
                        </TableCell>
                        <TableCell className="text-right py-6">
                           <div className="flex flex-col items-end gap-1">
                              <div className="text-4xl font-black text-blue-700 tracking-tighter">
                                {formatCurrency(totalAmount)}
                              </div>
                              <div className="h-1.5 w-full bg-blue-100 rounded-full mt-1" />
                           </div>
                        </TableCell>
                        <TableCell className="w-[50px]"></TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
                  No items added. Click &quot;Add Service&quot; to start.
                </div>
              )}

              {form.formState.errors.items && (
                <p className="text-sm font-medium text-destructive text-center mt-4">
                  {form.formState.errors.items.message}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Payment Status & Method */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Payment</CardTitle>
              <CardDescription>Configure payment settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <FormField
                control={form.control}
                name="paymentStatus"
                render={({ field }) => (
                  <FormItem className="space-y-3">
                    <FormLabel>Payment Status</FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        className="flex flex-col gap-3 sm:flex-row sm:gap-4"
                      >
                        <FormItem className="flex items-center space-x-3 space-y-0">
                          <FormControl>
                            <RadioGroupItem value="UNPAID" />
                          </FormControl>
                          <FormLabel className="font-normal cursor-pointer">Unpaid</FormLabel>
                        </FormItem>
                        <FormItem className="flex items-center space-x-3 space-y-0">
                          <FormControl>
                            <RadioGroupItem value="PAID" />
                          </FormControl>
                          <FormLabel className="font-normal cursor-pointer">Paid</FormLabel>
                        </FormItem>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {paymentStatus === 'PAID' && (
                <FormField
                  control={form.control}
                  name="paymentMethod"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                        Payment Method
                      </FormLabel>
                      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                        <Button
                          type="button"
                          variant={field.value === 'CASH' ? 'default' : 'outline'}
                          className={cn(
                            'h-auto py-3 flex-col gap-1 rounded-xl',
                            field.value === 'CASH' && 'bg-blue-600 hover:bg-blue-700'
                          )}
                          onClick={() => field.onChange('CASH')}
                        >
                          <Banknote className="h-5 w-5" />
                          <span className="text-[10px] font-bold">CASH</span>
                        </Button>
                        <Button
                          type="button"
                          variant={field.value === 'GOPAY' ? 'default' : 'outline'}
                          className={cn(
                            'h-auto py-3 flex-col gap-1 rounded-xl',
                            field.value === 'GOPAY' && 'bg-blue-600 hover:bg-blue-700'
                          )}
                          onClick={() => field.onChange('GOPAY')}
                        >
                          <Wallet className="h-5 w-5" />
                          <span className="text-[10px] font-bold">GOPAY</span>
                        </Button>
                        <Button
                          type="button"
                          variant={field.value === 'BANK_TRANSFER' ? 'default' : 'outline'}
                          className={cn(
                            'h-auto py-3 flex-col gap-1 rounded-xl',
                            field.value === 'BANK_TRANSFER' && 'bg-blue-600 hover:bg-blue-700'
                          )}
                          onClick={() => field.onChange('BANK_TRANSFER')}
                        >
                          <CreditCard className="h-5 w-5" />
                          <span className="text-[10px] font-bold">TRANSFER</span>
                        </Button>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Order Notes</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="General order notes..."
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Submit Buttons */}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end sm:gap-4">
            <Button
              type="button"
              variant="outline"
              className="w-full sm:w-auto"
              onClick={() => navigate({ to: '/orders', search: {} })}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createOrder.isPending}
              className="w-full min-w-[140px] sm:w-auto"
            >
              {createOrder.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Order'
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
